import { FAQItem, Review } from './types';

export const TRANSLATIONS = {
  EN: {
    nav: {
      why: 'Why us',
      fit: 'Fit Check',
      calculator: 'Calculator',
      pricing: 'Packages',
      reviews: 'Reviews',
      team: 'Team',
      faq: 'FAQ',
      articles: 'Articles',
      guides: 'Tax Guides',
      cta: 'I need help with my taxes',
      contact: 'Contact',
      legal: 'Legal',
      imprint: 'Imprint',
      privacy: 'Privacy Policy',
      gtc: 'General Terms and Conditions',
      digitalFirst: 'Digital-First (Austria)',
      rightsReserved: 'All rights reserved.',
      companyAddress: 'Biberhaufenweg 78, 1220 Vienna, Austria',
      kswMember: 'Member of the Chamber of Tax Advisers and Auditors (KSW), WT code 241511',
      supervisoryAuthority: 'Supervisory authority: Austrian Federal Ministry of Finance (BMF)',
      professionalLaw: 'Applicable professional law: Wirtschaftstreuhandberufsgesetz (WTBG 2017)'
    },
    hero: {
      headline: 'English-speaking tax support for the self-employed',
      subheadline: 'PSTax helps freelancers and one person businesses handle income tax, VAT, SVS, FinanzOnline and tax office communication — digitally, quickly and with fixed packages.',
      ctaFit: 'I need help with my taxes',
      ctaCalc: 'Try our tax calculator',
      trusted: 'Trusted by 600+',
      trustedSub: 'International Founders in Austria.',
      badge: 'Specialized in solo-proprietors & freelancers',
      googleBadge: '5.0 on Google · 70+ reviews',
      floatingBadgeTitle: 'Fast Response',
      floatingBadgeSub: 'Digital-first support',
      bullets: [
        'English-speaking support',
        'Digital workflow',
        'Fixed annual packages'
      ],
      advisorLabel: 'Your Advisor',
      role: 'Tax Advisor',
      partnerCta: 'Just starting out? Click here for a complete Setup Package.'
    },
    fitCheck: {
      title: 'Fit Check',
      progress: ['Income', 'Legal Form', 'Industry', 'Packages', 'Result'],
      header: "In order to book a free call, let's make sure we're the right fit for you!",
      stepLabel: 'Step',
      ofLabel: 'of',
      rejectionLabel: "Looks like we're not a fit.",
      rejectionMistake: 'Think we made a mistake?',
      rejectionEmail: "Reach out via email at office@pstax.at and tell us why you think we are a fit.",
      emailUs: 'Email Us',
      retry: 'Retry',
      startOver: 'Start Over',
      next: 'Next Step',
      back: 'Back',
      showResult: 'Show Result',
      calendly: 'https://calendly.com/pstax/free-consultation?hide_gdpr_banner=1',
      reasons: {
        income: "At PSTax we focus on self employed people. If you are self employed AND have another income source, we might be able to help you. If you are purely employed or have another type of income, we are not the right tax advisor for you.",
        legal: "At PSTax we focus on 1 person / sole proprietors and freelancers. We currently do not have the expertise and capacity to handle GmbH/OG/KG.",
        industry: "At PSTax we focus on service based businesses to ensure we truly provide them the best service and consulting possible. Therefore we don't work with clients from certain industries that you have selected.",
        default: "At PSTax, we specialize in digital-first services for modern sole proprietors in Austria. Based on your answers, our service focus doesn't align perfectly with your needs or industry."
      },
      steps: [
        {
          id: 'income',
          title: 'Type of Income',
          question: 'Which types of income do you have? (check all that apply)',
          options: [
            { key: 'self', label: 'Self employed / Business (or planning to)' },
            { key: 'employed', label: 'Employed' },
            { key: 'rental', label: 'Rental' },
            { key: 'capital', label: 'Capital' }
          ]
        },
        {
          id: 'legal',
          title: 'Legal Form',
          question: 'What is your current or planned legal form?',
          options: [
            { key: 'solo', label: '1 Person / Freelancer / Sole Proprietor' },
            { key: 'company', label: 'GmbH/OG/KG' }
          ]
        },
        {
          id: 'industry',
          title: 'Industry',
          question: "At PSTax we focus on service based businesses. Therefore we don't work with clients from certain industries. Do you work in any of these non-qualifying industries?",
          yes: 'Yes, I work in one of these',
          no: "No, I don't work in any of these",
          options: [
            'Crypto Trading',
            'Gastro / Cafe',
            'E-Commerce / Online Shops',
            'Construction / Building'
          ]
        },
        {
          id: 'packages',
          title: 'Did you see our packages?',
          question: "We currently only offer annual packages and don't have capacity for one off services. Package prices range from {range}.",
          options: [
            { key: 'confirm', label: 'That works for me!' }
          ]
        }
      ],
      rejection: {
        headline: 'Sorry, we are probably not the right fit.',
        text: 'PSTax specializes in self-employed individuals and freelancers in Austria. If you are not self-employed or your main activity is outside our focus, we cannot offer a free consultation.'
      },
      maybe: {
        headline: 'We may be able to help — but we need to check first.',
        text: 'Your situation may fit, but we need more details before offering a free consultation.'
      },
      good: {
        headline: 'Great! It looks like we\'re a perfect match!',
        text: 'Based on your answers, your case matches the type of clients we usually work with.',
        bookText: "We're excited to meet you! Please book a free call below."
      }
    },
    calculator: {
      title: 'Self-employed net profit calculator for Austria',
      subheadline: 'Estimate your income tax, SVS and approximate net income as a self-employed person in Austria.',
      disclaimer: 'Calculated based on {year} values. This is an estimate, your actual net income may vary.',
      revenueLabel: 'Annual Revenue (€)',
      expensesLabel: 'Expenses & Allowances',
      pauschaleLabel: 'Flat rate of {rate}% (Pauschale)',
      isPauschaleTooltip: 'Click to switch to actual expenses',
      actualExpensesLabel: 'Actual Expenses (€)',
      wfhLabel: 'I work from home',
      childrenLabel: 'Children',
      taxYearLabel: 'Tax Year',
      cta: "Need help? See if we're a good fit!",
      revenueHeader: 'Annual Revenue',
      expectedProfit: 'Expected Profit',
      svsLabel: 'Estimated SVS',
      gfbLabel: 'Profit Allowance (GFB)',
      taxableProfitLabel: 'Taxable Profit',
      taxLabel: 'Estimated Income Tax',
      annualNetLabel: 'Annual Net Profit',
      monthlyNetLabel: 'Monthly Net',
      effectiveRateLabel: 'Effective Tax Rate',
      pauschaleShort: 'Expenses (Pauschale)',
      actualShort: 'Actual Expenses',
      wfhShort: 'WFH Allowance',
      familyBonusShort: 'Familienbonus (Tax)',
      lead: {
        title: 'Detailed calculations explained',
        subtitle: 'Get your results by E-mail',
        description: 'Receive a full breakdown of your SVS contributions, tax brackets, and dynamic savings potentials directly to your inbox.',
        emailPlaceholder: 'your.email@example.com',
        consentCheckbox: 'I agree that PSTax may send me my calculation results and occasional tax updates via E-mail. I can unsubscribe at any time.',
        privacyPolicyLink: 'Privacy Policy',
        buttonSubmit: 'Send & Explain Results',
        buttonSubmitting: 'Sending...',
        successDoiSent: 'Confirmation sent! Please check your inbox and confirm.',
        successSent: 'Your calculation results are on their way!',
        errorGeneric: 'An error occurred. Please try again or contact us directly.',
        errorRateLimit: 'Too many requests. Please try again in an hour.'
      }
    },
    pricing: {
      title: 'Fixed Annual Packages',
      sub: 'Fixed annual fees for predictable planning. No hidden hourly rates for basic support.',
      interval: '/year',
      valueBox: {
        headline: 'Your package may cost <span class="text-pstax-blue">less</span> than you think',
        subheadline: 'Through a combination of <span class="font-bold text-pstax-navy italic">new starter credits</span>, <span class="font-bold text-pstax-navy italic">tax deductions</span> and <span class="font-bold text-pstax-navy italic">active optimization</span>, our packages often pay for themselves.',
        trustedText: 'Fixed price. No surprises. Someone to ask when tax questions come up.',
        cta: 'Check if we fit',
        disclaimer: 'Example is simplified and depends on your individual income and tax situation. The € 250 credit depends on eligibility and availability under the KSW initiative.',
        items: [
          {
            title: "€ 250 starter credit",
            desc: "New to self-employment? Check out <a href=\"https://niemals-ohne.at\" target=\"_blank\" rel=\"noopener noreferrer\" class=\"text-pstax-blue hover:underline\">niemals-ohne.at</a> to apply for a voucher."
          },
          {
            title: "Often pays for itself",
            desc: "Optimization, SVS planning and avoiding mistakes usually saves more than it costs."
          },
          {
            title: "Tax deduction",
            desc: "The € 1.200 Standard package gives a € 480 tax deduction at a 40 % tax rate."
          },
          {
            title: "Peace of mind included",
            desc: "Full reliability for all our clients: no missed deadlines and no more tax worries."
          }
        ]
      },
      starter: {
        name: 'Starter',
        price: '800',
        desc: 'Ready to scale your side-hustle but making less than 10K per year (not in SVS)',
        features: [
          'Annual income tax return',
          'Initial tax office registration (Verf24)',
          'Employed income and advertising expenses',
          'Family related tax benefits',
          'Bookkeeping and invoice template',
          'Tax office correspondence',
          'E-mail support throughout the year (fair use)'
        ]
      },
      standard: {
        name: 'Standard',
        price: '1.200',
        desc: 'You are (planning to be) self employed earning above the SVS threshold and are not claiming input VAT',
        features: [
          'Everything in Starter',
          'SVS registration and optimization',
          'Quarterly tax payment and deadline reminders',
          'Year end tax optimization',
          'VAT ID registration (no input VAT claiming)',
          'Quarterly EC Sales Listing for EU B2B income for a fee',
          'Guaranteed 48 hour e-mail responses'
        ],
        effectiveLabel: 'Effective cost: approx. € 720 after tax*'
      },
      pro: {
        name: 'Professional',
        price: '1.600',
        desc: 'You are (planning to be) self employed earning above the SVS threshold and are claiming input VAT',
        features: [
          'Everything in Standard',
          'Annual VAT return',
          'VAT optimization and support',
          'Input VAT deduction',
          'Quarterly VAT returns',
          'Quarterly EC Sales Listing for EU B2B income',
          'Guaranteed 48 hour e-mail responses'
        ]
      },
      note: '* All prices include 20% VAT. Fees are usually tax deductible. Effective cost example based on Standard package and 40% income tax rate. Individual results vary.'
    },
    howItWorks: {
      title: 'How We Work Together: Next Steps',
      subtitle: 'Our streamlined digital process to get your taxes sorted - simple and stress free!',
      cta: 'Start Fit Check',
      note: 'Takes less than 2 minutes',
      steps: [
        {
          title: 'Fit Check & book call',
          desc: 'First, complete our quick Fit Check. If we are a match, you can book a free introduction call directly in our calendar.'
        },
        {
          title: 'Consultation & offer',
          desc: 'We get to know each other and answer first questions. Please keep your ID ready for this. If it\'s a fit, we\'ll make you an offer.'
        },
        {
          title: 'Onboarding',
          desc: 'After you give us power of attorney we set ourselves as your tax advisor in FinanzOnline. This is also when we share our invoice.'
        },
        {
          title: 'Send us your data',
          desc: 'You send us your income and expenses or fill our Google sheets bookkeeping template so that we can prepare your taxes for the year.'
        },
        {
          title: 'Tax filing & support',
          desc: 'We prepare your tax return, provide ongoing email support throughout the year and optimize your situation at year-end.'
        }
      ]
    },
    common: {
      footer: 'PSTax is a modern, digital-first Austrian tax advisory firm. We help self-employed professionals, freelancers, and service-businesses navigate the tax system with ease.'
    },
    carousel: {
      title: 'Why PSTax is your best partner',
      sub: "We understand the specific challenges and anxieties that come with being self-employed in Austria. We don't just 'do taxes' – we provide peace of mind.",
      slides: [
        {
          id: 'bureaucracy',
          problem: {
            tag: "THE SCARES",
            title: "Austrian Bureaucracy & Tax Anxiety",
            desc: "Missing a critical SVS deadline, getting an unexpected € 2.000 penalty, or overpaying taxes by thousands because you missed a deduction rule you didn't know existed."
          },
          solution: {
            tag: "PSTAX GUARDIANSHIP",
            title: "Total Peace of Mind",
            desc: "We track every deadline, handle all Finanzamt communication, and proactively optimize your tax and social security. We act as your shield against bureaucracy so you never have to worry about confusing letters again."
          }
        },
        {
          id: 'language',
          problem: {
            tag: "COMMUNICATION",
            title: "The 'Amtsdeutsch' Barrier",
            desc: "Being new in the country and receiving 5-page letters in complex German from the tax office. Even with Google Translate, you're never quite sure what they actually want from you."
          },
          solution: {
            tag: "ENGLISH-FIRST SUPPORT",
            title: "Talk to us in English",
            desc: "Over 50% of our clients are non-German natives. We explain everything in clear English. Need help with a weird letter? Just forward it to us – we'll translate the action items for you."
          }
        },
        {
          id: 'pricing',
          problem: {
            tag: "COSTS",
            title: "Surprise Invoices & Hourly Traps",
            desc: "Traditional tax advisors often bill by the minute. You call for a 5-minute question and receive a surprise € 150 invoice two weeks later. It makes you afraid to talk to your own advisor."
          },
          solution: {
            tag: "FIXED PRICING",
            title: "Zero Hidden Fees",
            desc: "We work with fixed annual packages. You know your costs before you even start. No billing per email, no surprise 'consultation' fees. Budgeting for your business has never been easier."
          }
        },
        {
          id: 'questions',
          problem: {
            tag: "SUPPORT",
            title: "The 'Guessing' Risk",
            desc: "Because you're worried about the clock ticking on billable hours, you guess how to categorize an expense or how to draft an invoice – and end up with a mess at the end of the year."
          },
          solution: {
            tag: "OPEN DOOR POLICY",
            title: "Ask anything, anytime",
            desc: "We encourage questions. We'd rather spend 5 minutes answering you now than 5 hours fixing a mistake later. It's all covered in your package. No hesitation, no barriers."
          }
        },
        {
          id: 'specialization',
          problem: {
            tag: "FOCUS",
            title: "The Generalist Gap",
            desc: "Most tax firms focus on GmbHs and large corporations. As a solo freelancer, you're often their 'smallest' client and don't get the specialized expertise your unique situation requires."
          },
          solution: {
            tag: "FREELANCER SPECIALISTS",
            title: "We speak 'Freelance'",
            desc: "We focus 100% on self-employed professionals and one-person businesses. We know every specific deduction, every SVS quirk, and every optimization strategy relevant to your world."
          }
        }
      ]
    },
    team: {
      title: 'Meet the Team',
      sub: 'Experienced professionals dedicated to making your business tax-compliant and optimized in Austria.',
      members: [
        {
          name: 'Pascal Schraml',
          role: 'Managing Director & Tax Advisor',
          desc: 'Pascal leads PSTax and specializes in tax advisory for self-employed people, freelancers and digital service businesses in Austria.',
          image: '/pascal-team.webp',
          zoom: 1.45,
          focusX: 42,
          focusY: 40
        },
        {
          name: 'Julia Kauer',
          role: 'Operations & Communications',
          desc: 'Julia helps build PSTax\'s internal backbone — processes, workflows, team. She also makes sure self-employed people find us before tax stress kicks in.',
          image: '/julia-team.webp',
          zoom: 1.90,
          focusX: 52,
          focusY: 43
        },
        {
          name: 'Evelyn Pfannhauser',
          role: 'Tax Advisor Candidate',
          desc: 'Evelyn supports clients with tax returns, VAT topics, research and communication with Austrian authorities.',
          image: '/evelyn.webp',
          zoom: 1.20,
          focusX: 50,
          focusY: 30
        },
        {
          name: 'Lisa Thaller',
          role: 'Tax Advisor Candidate',
          desc: 'Lisa supports all ongoing tax matters and the preparation of tax returns — carefully, reliably and with attention to detail.',
          image: '/lisa.webp',
          zoom: 1.20,
          focusX: 50,
          focusY: 28
        },
        {
          name: 'Thomas Wöpperer',
          role: 'Technology, Security & Automation',
          desc: 'Thomas manages security, internal systems and development to keep our digital workflows reliable, efficient and secure.',
          image: '/thomas.webp',
          zoom: 1.24,
          focusX: 50,
          focusY: 47
        }
      ]
    },
    reviews: {
      title: 'Client Reviews',
      sub: 'What our clients say about working with PSTax.',
      items: [
        {
          name: 'Ana T.',
          role: 'Dancer',
          text: 'Excellent! Fast replies, very helpful, simple and clear instructions, advices and clarifications for any question you might have. The team works fast, gives security and feels like being taken care of in the world of papers. :)  Totally Recommended!',
          rating: 5
        },
        {
          name: 'Amaia S.',
          role: 'Leadership Coach & Trainer',
          text: 'I could not recommend this tax advisor more. Not only is Pascal great at what he does, but he gives you such a sense of support, care and professionality that you feel reassured - which is so helpful when you are an entrepreneur! The human aspect he brings to the table makes the whole difference.',
          rating: 5
        },
        {
          name: 'Hannes H.',
          role: 'Pflegegewerbe',
          text: 'Ich würde PSTax jedem weiterempfehlen. Pascal und sein Team sind immer für einen da, wenn man Fragen hat. Es ist immer wieder erstaunlich wie schnell die Antworten zu gestellten Fragen kommen. Auch die Motivation die er ausstrahlt ist ansteckend. Bitte weiter so!',
          rating: 5
        },
        {
          name: 'Jo-Ann B.',
          role: 'Product & UX Designer',
          text: 'Great experience with PSTax. Pascal is knowledgeable, professional, and incredibly kind. As an expat in Vienna, I appreciated his clear advice and support throughout the process. Highly recommended!',
          rating: 5
        },
        {
          name: 'Rory O.',
          role: 'Musician and producer',
          text: 'Moving to Austria was a big change, but Pascal’s expert support with Austrian taxes, social security, and my UK rental income made the transition smooth. PSTax’s professionalism and international tax expertise make them a highly recommended partner for anyone in a similar situation.',
          rating: 5,
          image: '/rory.webp'
        },
        {
          name: 'Fabio M.',
          role: 'Researcher',
          text: 'What I like is that the service is reliable and fast, with no hassles from my side. I sit back and relax.',
          rating: 5
        }
      ]
    },
    // Google reviews (home page): original wording, shortened only with "…". Source: our Google Business Profile.
    googleReviews: {
      rating: "5.0 on Google",
      disclaimer: "Selected reviews from our Google Business Profile.",
      showAll: "Show all reviews",
      starsLabel: "5 out of 5 stars",
      carouselLabel: "Reviews on Google",
      items: [
        { name: "Jamie Scott", text: "I moved to Austria in 2023. And fair to say that when you barely understand a word of German, the Austrian tax and social insurance system is impenetrable. … I found Pascal and the PSTax team. I have not really had to think about tax and social insurance since." },
        { name: "Lulu Lab", text: "Great experience with PS Tax. They explained everything in a way that actually made sense. Quick to reply, no nonsense, and I never felt like a silly question was too silly. Would recommend to anyone who wants things explained properly." },
        { name: "Ivan Bachev", text: "A year ago, I decided to open a business in Austria. … Everything went smoothly, and they helped me with everything needed for registering a business license and handling social security as a self-employed professional. … highly recommend them to those looking to start a business in Austria." },
        { name: "Maria Pikalova", text: "I'm very happy with the service provided by PSTax. I especially appreciate their modern and user-friendly client portal, which gives a clear financial overview and helps you see how much to set aside for taxes and SVS contributions. … Highly recommended!" },
        { name: "Claire", text: "Pascal has been an enormous help in navigating the tax setup for my business activities in Austria and for international work. … He takes the time to understand the actual situation rather than giving generic answers … Highly recommended for anyone looking for knowledgeable, approachable and practical advice." },
        { name: "Paula M", text: "Highly recommend 💯 They're fast, English-speaking and super friendly. But my favorite part is that I don't have to stress about SVS, Finanzamt because their team handles everything for me." },
        { name: "Michael Goldrei", text: "I used PSTax for the first time this year and they did an excellent job. Very professional, quick to respond … my previous tax advisor dropped out of doing my tax return two days before the deadline … PSTax immediately got on the case and did it in time!" },
        { name: "Kimi Päivärinta", text: "Even in the AI era, a good tax advisor is absolutely worth it: they have practical, money-saving knowledge you simply won't find by Googling or asking AI. … They proactively share useful tips and always answer my questions quickly, patiently, and comprehensively." },
        { name: "Camilla Pletuhina-Tonev", text: "PSTax provides an exceptional service. Pascal is incredibly competent, involved, and solution-oriented. Even my husband - who doesn't work with PSTax - tends to say, \"We should ask Pascal,\" whenever we feel uncertain about all things tax- or SVS-related. …" },
        { name: "Marta S", text: "Pascal is one of the most knowledgeable tax advisors I know when it comes to self-employment in Austria. 👌 He is also extremely responsive, reliable and easy to communicate with. His English is excellent. …" },
      ]
    },
    industries: {
      title: 'Who we usually work with',
      sub: 'We specialize in solo professional services and creative industries across Austria.',
      items: [
        'IT freelancers', 'Software developers', 'Tech consultants', 'Marketing consultants',
        'Designers', 'Advertising & branding professionals', 'Business consultants', 'Coaches & trainers',
        'Psychotherapists', 'Physiotherapists', 'Health service professionals', 'Lawyers',
        'Content creators', 'Video / media producers', 'Writers & translators', 'Other solo service businesses'
      ]
    },
    knowledgeHub: {
      tag: 'Frequently asked questions',
      title: 'Working with PSTax and in Austria',
      sub: 'How we work together and simple explanations to the most frequent tax topics for self-employed professionals.',
      footerTitle: 'Still have questions?',
      footerSub: "See if we're a good fit to work with you and answer them.",
      footerCta: 'Start Fit Check',
      categories: [
        'Working with PSTax',
        'Registering in Austria',
        'Income Tax',
        'Social Security',
        'VAT',
        'Bookkeeping and invoices'
      ]
    }
  },
  DE: {
    nav: {
      why: 'Warum wir',
      fit: 'Fit Check',
      calculator: 'Rechner',
      pricing: 'Pakete',
      reviews: 'Bewertungen',
      team: 'Team',
      faq: 'FAQ',
      articles: 'Artikel',
      guides: 'Tax Guides',
      cta: 'Ich brauche Hilfe bei den Steuern',
      contact: 'Kontakt',
      legal: 'Rechtliches',
      imprint: 'Impressum',
      privacy: 'Datenschutz',
      gtc: 'AGB',
      digitalFirst: 'Digital-First (Österreich)',
      rightsReserved: 'Alle Rechte vorbehalten.',
      companyAddress: 'Biberhaufenweg 78, 1220 Wien, Österreich',
      kswMember: 'Mitglied der Kammer der Steuerberater:innen und Wirtschaftsprüfer:innen (KSW), WT-Code 241511',
      supervisoryAuthority: 'Aufsichtsbehörde: Bundesministerium für Finanzen (BMF)',
      professionalLaw: 'Anwendbares Berufsrecht: Wirtschaftstreuhandberufsgesetz (WTBG 2017)'
    },
    hero: {
      headline: 'Online Steuerberatung für Einzelunternehmen',
      subheadline: 'PSTax ist dein digitaler Partner für Einkommensteuer, SVS und FinanzOnline - komplett digital, papierlos und mit transparenten Fixpreis-Paketen.',
      ctaFit: 'Ich brauche Hilfe bei den Steuern',
      ctaCalc: 'Zum Steuerrechner',
      trusted: 'Über 600',
      trustedSub: 'zufriedene Selbstständige in ganz Österreich.',
      badge: 'Spezialisiert auf Ein-Personen-Unternehmen und Freelancer',
      googleBadge: '5,0 auf Google · 70+ Bewertungen',
      floatingBadgeTitle: 'Schnelle Rückmeldung',
      floatingBadgeSub: 'Digitaler Support',
      bullets: [
        '100% online & digital',
        'verständlich erklärt',
        'All inclusive Paket-Preise'
      ],
      advisorLabel: 'Dein Berater',
      role: 'Steuerberater'
    },
    fitCheck: {
      title: 'Fit Check',
      progress: ['Einkommen', 'Rechtsform', 'Branche', 'Pakete', 'Ergebnis'],
      header: "Passen wir zusammen? Dann kannst du ein gratis Erstgespräch buchen!",
      stepLabel: 'Schritt',
      ofLabel: 'von',
      rejectionLabel: 'Sieht so aus, als würden wir nicht zusammenpassen.',
      rejectionMistake: 'Glaubst du, wir haben einen Fehler gemacht?',
      rejectionEmail: 'Schreib uns eine E-Mail an office@pstax.at und erklär uns kurz deine Situation.',
      emailUs: 'E-Mail schreiben',
      retry: 'Nochmal versuchen',
      startOver: 'Neu starten',
      next: 'Weiter',
      back: 'Zurück',
      showResult: 'Ergebnis anzeigen',
      calendly: 'https://calendly.com/pstax/30min?hide_gdpr_banner=1',
      reasons: {
        income: "PSTax fokussiert sich auf Selbstständige. Wenn du selbstständig bist UND andere Einkünfte hast, können wir dir helfen. Wenn du garnicht selbstständig bist, sind wir nicht die richtige Steuerberatung für dich.",
        legal: "PSTax fokussiert sich auf Einzelunternehmer und Freelancer. GmbHs/OGs/KGs können wir aktuell nicht betreuen.",
        industry: "Wir fokussieren uns auf Dienstleistungs-Branchen, um beste Qualität zu garantieren. Branchen wie Krypto-Trading, Gastro oder Bauwesen betreuen wir daher nicht.",
        default: "Basierend auf deinen Antworten passt unser Service-Fokus aktuell leider nicht zu deinen Bedürfnissen oder deiner Branche."
      },
      steps: [
        {
          id: 'income',
          title: 'Art des Einkommens',
          question: 'Welche Einkommensarten hast du? (Mehrfachwahl möglich)',
          options: [
            { key: 'self', label: 'Selbstständig / Gewerbe (oder geplant)' },
            { key: 'employed', label: 'Angestellt' },
            { key: 'rental', label: 'Vermietung & Verpachtung' },
            { key: 'capital', label: 'Kapitalvermögen' }
          ]
        },
        {
          id: 'legal',
          title: 'Rechtsform',
          question: 'Was ist deine aktuelle oder geplante Rechtsform?',
          options: [
            { key: 'solo', label: 'Einzelunternehmen / Freelancer / Neue Selbstständige' },
            { key: 'company', label: 'GmbH/OG/KG' }
          ]
        },
        {
          id: 'industry',
          title: 'Branche',
          question: "PSTax ist auf Dienstleistungsunternehmen spezialisiert. Bestimmte Branchen betreuen wir daher nicht. Arbeitest du in einer dieser Branchen?",
          yes: 'Ja, ich arbeite in einer dieser Branchen',
          no: 'Nein, ich arbeite in keiner dieser Branchen',
          options: [
            'Krypto-Trading',
            'Gastronomie / Cafe',
            'E-Commerce / Online-Shops',
            'Bauwesen / Handwerk'
          ]
        },
        {
          id: 'packages',
          title: 'Hast du unsere Pakete gesehen?',
          question: 'Wir bieten derzeit ausschließlich Jahrespakete und keine einmaligen Beratungen an. Die Pakete reichen von {range}.',
          options: [
            { key: 'confirm', label: 'Das passt für mich!' }
          ]
        }
      ],
      rejection: {
        headline: 'Leider passen wir aktuell nicht zusammen.',
        text: 'PSTax spezialisiert sich auf Einzelunternehmer und Freelancer in Österreich. Wenn du nicht selbstständig bist oder deine Haupttätigkeit außerhalb unseres Fokus liegt, können wir keine kostenlose Beratung anbieten.'
      },
      maybe: {
        headline: 'Vielleicht können wir helfen – wir müssen das erst prüfen.',
        text: 'Deine Situation könnte passen, aber wir benötigen mehr Details vor einem Erstgespräch.'
      },
      good: {
        headline: 'Sieht so aus, als würden wir gut zusammenpassen!',
        text: 'Basierend auf deinen Antworten passt dein Fall genau zu den Klient:innen, die wir normalerweise betreuen.',
        bookText: 'Wir freuen uns darauf, dich kennenzulernen! Bitte buche unten deinen Termin.'
      }
    },
    calculator: {
      title: 'Steuer-Rechner für Selbstständige in Österreich',
      subheadline: 'Schätze deine Einkommensteuer, SVS und dein ungefähres Netto-Einkommen als Einzelunternehmer:in.',
      disclaimer: 'Berechnet mit den Werten für {year}. Dies ist eine Schätzung, dein tatsächliches Netto kann variieren.',
      revenueLabel: 'Jährlicher Umsatz (€)',
      expensesLabel: 'Ausgaben & Freibeträge',
      pauschaleLabel: 'Anwendbare Pauschale {rate}%',
      isPauschaleTooltip: 'Klicken, um zu tatsächlichen Ausgaben zu wechseln',
      actualExpensesLabel: 'Tatsächliche Ausgaben (€)',
      wfhLabel: 'Ich arbeite im Home Office',
      childrenLabel: 'Kinder',
      taxYearLabel: 'Steuerjahr',
      cta: 'Hilfe? Schau, ob wir zusammen passen!',
      revenueHeader: 'Jährlicher Umsatz',
      expectedProfit: 'Voraussichtlicher Gewinn',
      svsLabel: 'Geschätzte SVS',
      gfbLabel: 'Gewinnfreibetrag (GFB)',
      taxableProfitLabel: 'Steuerpflichtiger Gewinn',
      taxLabel: 'Geschätzte Einkommensteuer',
      annualNetLabel: 'Jährlicher Netto-Gewinn',
      monthlyNetLabel: 'Monatliches Netto',
      effectiveRateLabel: 'Effektive Steuerlast',
      pauschaleShort: 'Ausgaben (Pauschale)',
      actualShort: 'Tatsächliche Ausgaben',
      wfhShort: 'Home Office Pauschale',
      familyBonusShort: 'Familienbonus (Steuer)',
      lead: {
        title: 'Berechnungen im Detail erklären lassen',
        subtitle: 'Ergebnisse per E-Mail erhalten',
        description: 'Erhalte eine übersichtliche Aufstellung deiner SVS-Beiträge, Steuerstufen und individuellen Sparpotenziale direkt in dein Postfach.',
        emailPlaceholder: 'deine.email@beispiel.at',
        consentCheckbox: 'Ich stimme zu, dass PSTax mir meine Berechnungsergebnisse und gelegentliche Steuer-Updates per E-Mail zusendet. Ich kann mich jederzeit abmelden.',
        privacyPolicyLink: 'Datenschutzerklärung',
        buttonSubmit: 'Ergebnisse senden & erklären',
        buttonSubmitting: 'Wird gesendet...',
        successDoiSent: 'Bestätigung geschickt! Bitte überprüfe dein Postfach und bestätige den Link.',
        successSent: 'Deine Berechnungsergebnisse sind auf dem Weg!',
        errorGeneric: 'Ein Fehler ist aufgetreten. Bitte versuche es erneut oder kontaktiere uns direkt.',
        errorRateLimit: 'Zu viele Anfragen. Bitte versuche es in einer Stunde erneut.'
      }
    },
    pricing: {
      title: 'Einfache und transparente Pakete',
      sub: 'Fixe Jahrespakete für planbare Kosten. Keine versteckten Stundensätze für Standard-Support.',
      interval: '/Jahr',
      valueBox: {
        headline: 'Dein Paket kostet vielleicht <span class="text-pstax-blue">weniger</span> als du denkst',
        subheadline: 'Durch eine Kombination aus <span class="font-bold text-pstax-navy italic">Gründungsbonus</span>, <span class="font-bold text-pstax-navy italic">Steuerabzügen</span> und <span class="font-bold text-pstax-navy italic">aktiver Optimierung</span> zahlen sich unsere Pakete oft von selbst.',
        trustedText: 'Fixpreis. Keine Überraschungen. Ein Ansprechpartner für alle Steuerfragen.',
        cta: 'Zum Fit Check',
        disclaimer: 'Beispiel vereinfacht und abhängig von individuellen Einkünften. Der € 250 Bonus ist abhängig von Verfügbarkeit und Förderkriterien der KSW-Initiative.',
        items: [
          {
            title: "€ 250 Gründungsbonus",
            desc: "Neu in der Selbstständigkeit? Auf <a href=\"https://niemals-ohne.at\" target=\"_blank\" rel=\"noopener noreferrer\" class=\"text-pstax-blue hover:underline\">niemals-ohne.at</a> kannst du einen Gutschein für Beratung beantragen."
          },
          {
            title: "Zahlt sich oft von selbst",
            desc: "Steueroptimierung, SVS-Planung und vermiedene Fehler sparen meist mehr, als die Beratung kostet."
          },
          {
            title: "Steuerlich absetzbar",
            desc: "Das € 1.200 Standard-Paket bringt bei 40% Steuersatz ca. € 480 Steuerersparnis."
          },
          {
            title: "Sicherheit inklusive",
            desc: "Volle Zuverlässigkeit für unsere Klient:innen: keine verpassten Fristen und keine Steuersorgen mehr."
          }
        ]
      },
      starter: {
        name: 'Starter',
        price: '800',
        desc: 'Du willst deinen Side-Hustle skalieren und verdienst unter € 10.000 (unter SVS-Grenze)',
        longDesc: 'Für Einsteiger oder Geringverdiener, die ein sauberes Setup, die Einkommensteuererklärung und grundlegende Anleitung benötigen.',
        features: [
          'Jährliche Einkommensteuererklärung',
          'Erstmalige Finanzamtsmeldung (Verf24)',
          'Angestelltentätigkeit & Werbungskosten',
          'Familienbezogene Steuerbegünstigungen',
          'Buchhaltungs- & Rechnungsvorlage',
          'Korrespondenz mit dem Finanzamt',
          'E-Mail-Support das ganze Jahr (Fair Use)'
        ]
      },
      standard: {
        name: 'Standard',
        price: '1.200',
        desc: 'Du bist (oder planst es) selbstständig, verdienst über der SVS Grenze und hast keinen Vorsteuerabzug.',
        features: [
          'Alles aus dem Starter-Paket',
          'SVS-Anmeldung und Optimierung',
          'Quartalsweise Fristen- und Zahlungserinnerungen',
          'Steueroptimierung zum Jahresende',
          'UID Registrierung (kein Vorsteuerabzug)',
          'Quartalsweise Zusammenfassende Meldungen für EU B2B Einkommen gegen Gebühr',
          'Garantierte E-Mail Rückmeldungen in 48 Stunden.'
        ],
        effectiveLabel: 'Effektive Kosten: ca. 720 € nach Steuern*'
      },
      pro: {
        name: 'Pro',
        price: '1.600',
        desc: 'Du bist (oder planst es) selbstständig, verdienst über der SVS Grenze und hast Vorsteuerabzug.',
        features: [
          'Alles aus dem Standard-Paket',
          'Jährliche Umsatzsteuererklärung',
          'USt Optimierung und Support',
          'Abzug der Vorsteuer',
          'Quartalsweise Umsatzsteuervoranmeldungen',
          'Quartalsweise Zusammenfassende Meldungen für EU B2B Einkommen',
          'Garantierte E-Mail Rückmeldungen in 48 Stunden.'
        ]
      },
      note: '* Alle Preise inkl. 20% USt. Honorare sind steuerlich absetzbar. Effektive Kosten basieren auf Standard-Paket und 40% Grenzhonorarsatz.'
    },
    howItWorks: {
      title: 'Zusammenarbeit: Die nächsten Schritte',
      subtitle: 'Unser schlanker digitaler Prozess, um deine Steuern zu regeln – einfach und stressfrei!',
      cta: 'Fit Check starten',
      note: 'Dauert weniger als 2 Minuten',
      steps: [
        { 
          title: 'Fit Check & Termin', 
          desc: 'Absolviere zuerst unseren Fit-Check. Wenn wir zusammenpassen, kannst du direkt ein Erstgespräch in unserem Kalender buchen.' 
        },
        { 
          title: 'Erstgespräch & Angebot', 
          desc: 'Wir lernen uns kennen und klären erste Fragen. <span class="font-bold text-pstax-navy underline decoration-pstax-blue/30 decoration-2 underline-offset-4">Bitte halte dafür deinen Ausweis bereit.</span> Wenn es passt, erstellen wir ein Angebot.' 
        },
        { 
          title: 'Onboarding', 
          desc: 'Sobald du uns die Vollmacht erteilst, melden wir uns als dein Steuerberater bei FinanzOnline an. Das ist auch der Zeitpunkt an dem wir unsere Rechnung schicken.' 
        },
        { 
          title: 'Datenübermittlung', 
          desc: 'Du schickst uns deine Einnahmen und Ausgaben oder nutzt unsere Google Sheets Vorlage, damit wir deine Steuererklärungen vorbereiten können.' 
        },
        { 
          title: 'Einreichung & Support', 
          desc: 'Wir erstellen deine Steuererklärungen, bieten laufenden E-Mail-Support und optimieren deine Situation zum Jahresende.' 
        }
      ]
    },
    common: {
      footer: 'PSTax ist eine moderne, digital-orientierte österreichische Steuerberatungskanzlei. Wir helfen Selbstständigen und Freelancern, das Steuersystem mit Leichtigkeit zu meistern.'
    },
    carousel: {
      title: 'Warum PSTax dein bester Partner ist',
      sub: 'Wir verstehen die spezifischen Herausforderungen und Sorgen kleiner Selbstständiger in Österreich. Wir machen nicht nur Steuern – wir bringen Sorgenfreiheit.',
      slides: [
        {
          id: 'bureaucracy',
          problem: {
            tag: "ANGSTFAKTOR",
            title: "Österreichische Bürokratie & Steuerangst",
            desc: "Eine kritische SVS-Deadline verpassen, eine unerwartete Strafe von € 2.000 erhalten oder Tausende Euro zu viel Steuern zahlen, weil man eine Absetzmöglichkeit nicht kannte."
          },
          solution: {
            tag: "PSTAX SCHUTZSCHILD",
            title: "Volle Sicherheit",
            desc: "Wir überwachen jede Frist, erledigen die gesamte Kommunikation mit dem Finanzamt und optimieren proaktiv deine Steuern und Sozialversicherung. Wir sind dein Bürokratie-Schutzschild."
          }
        },
        {
          id: 'language',
          problem: {
            tag: "KOMMUNIKATION",
            title: "Die Barriere 'Amtsdeutsch'",
            desc: "Finanzamt, SVS und FinanzOnline schreiben oft komplizierter, als es sein müsste. Auch wenn Deutsch deine Muttersprache ist, bleibt oft unklar: Was ist gemeint? Was musst du tun? Bis wann?"
          },
          solution: {
            tag: "Verständliche Antworten",
            title: "Wir übersetzen Behörden-Sprache",
            desc: "Wir erklären dir Behördenbriefe verständlich und sagen dir konkret, was als Nächstes zu tun ist."
          }
        },
        {
          id: 'pricing',
          problem: {
            tag: "KOSTEN",
            title: "Überraschungsrechnungen & Honorarfallen",
            desc: "Klassische Steuerberatung rechnet oft minutengenau ab. Man ruft kurz an und erhält zwei Wochen später eine Rechnung über € 150. Das macht Angst, mit dem eigenen Beratungs-Team zu sprechen."
          },
          solution: {
            tag: "FIXPREISE",
            title: "Keine versteckten Gebühren",
            desc: "Wir arbeiten mit fixen Jahrespaketen. Du kennst deine Kosten im Voraus. Keine Abrechnung pro E-Mail, keine Überraschungen. Budgetierung war noch nie so einfach."
          }
        },
        {
          id: 'questions',
          problem: {
            tag: "UNTERSTÜTZUNG",
            title: "Das Risiko des Ratens",
            desc: "Aus Angst vor dem Ticken der Gebührenuhr rätst du, wie du eine Ausgabe kategorisierst oder eine Rechnung schreibst – und endest mit einem Chaos am Jahresende."
          },
          solution: {
            tag: "OPEN DOOR POLICY",
            title: "Frag uns alles, jederzeit",
            desc: "Wir liebe Fragen! Wir verbringen lieber 5 Minuten damit, dir jetzt zu antworten, als 5 Stunden damit, später einen Fehler zu korrigieren. Alles in deinem Paket enthalten."
          }
        },
        {
          id: 'specialization',
          problem: {
            tag: "FOKUS",
            title: "Die Generalisten-Lücke",
            desc: "Die meisten Kanzleien fokussieren sich auf GmbHs und Konzerne. Als Einzelunternehmen bist du oft ihr kleinster Klient und erhältst nicht die spezialisierte Expertise, die du brauchst."
          },
          solution: {
            tag: "FREELANCER-SPEZIALISTEN",
            title: "Wir sprechen 'Freelance'",
            desc: "Wir fokussieren uns zu 100% auf Selbstständige und Ein-Personen-Unternehmen. Wir kennen jede Absetzung, jede SVS-Eigenheit und jede Optimierung für deine Welt."
          }
        }
      ]
    },
    team: {
      title: 'Das Team',
      sub: 'Erfahrene Profis, die sich dafür einsetzen, dass dein Business in Österreich steuerlich konform und optimiert ist.',
      members: [
        {
          name: 'Pascal Schraml',
          role: 'Geschäftsführer & Steuerberater',
          desc: 'Pascal leitet PSTax und ist spezialisiert auf die Steuerberatung für Selbstständige, Freelancer und digitale Dienstleistungsunternehmen in Österreich.',
          image: '/pascal-team.webp',
          zoom: 1.45,
          focusX: 42,
          focusY: 40
        },
        {
          name: 'Julia Kauer',
          role: 'Organisation & Kommunikation',
          desc: 'Julia baut die internen Strukturen von PSTax mit auf: Abläufe, Workflows, Team. Daneben sorgt sie dafür, dass Selbstständige uns finden, bevor der Steuerstress beginnt.',
          image: '/julia-team.webp',
          zoom: 1.90,
          focusX: 52,
          focusY: 43
        },
        {
          name: 'Evelyn Pfannhauser',
          role: 'Steuerberater-Anwärterin',
          desc: 'Evelyn betreut Klient:innen bei Steuererklärungen, Umsatzsteuerthemen, Recherche und der Kommunikation mit Behörden.',
          image: '/evelyn.webp',
          zoom: 1.20,
          focusX: 50,
          focusY: 30
        },
        {
          name: 'Lisa Thaller',
          role: 'Steuerberater-Anwärterin',
          desc: 'Lisa unterstützt bei allen laufenden steuerlichen Themen und der Vorbereitung von Steuererklärungen – sorgfältig, engagiert und mit Blick fürs Detail.',
          image: '/lisa.webp',
          zoom: 1.20,
          focusX: 50,
          focusY: 28
        },
        {
          name: 'Thomas Wöpperer',
          role: 'Technology, Security & Automation',
          desc: 'Thomas kümmert sich um Security, interne Systeme und Entwicklung, damit unsere digitalen Abläufe sicher, effizient und zuverlässig funktionieren.',
          image: '/thomas.webp',
          zoom: 1.24,
          focusX: 50,
          focusY: 47
        }
      ]
    },
    reviews: {
      title: 'Klient:innen Feedback',
      sub: 'Was unsere Klient:innen über die Zusammenarbeit mit PSTax sagen.',
      items: [
        {
          name: 'Ana T.',
          role: 'Tänzerin',
          text: 'Excellent! Fast replies, very helpful, simple and clear instructions, advices and clarifications for any question you might have. The team works fast, gives security and feels like being taken care of in the world of papers. :)  Totally Recommended!',
          rating: 5
        },
        {
          name: 'Amaia S.',
          role: 'Leadership Coach & Trainer',
          text: 'I could not recommend this tax advisor more. Not only is Pascal great at what he does, but he gives you such a sense of support, care and professionality that you feel reassured - which is so helpful when you are an entrepreneur! The human aspect he brings to the table makes the whole difference.',
          rating: 5
        },
        {
          name: 'Hannes H.',
          role: 'Pflegegewerbe',
          text: 'Ich würde PSTax jedem weiterempfehlen. Pascal und sein Team sind immer für einen da, wenn man Fragen hat. Es ist immer wieder erstaunlich wie schnell die Antworten zu gestellten Fragen kommen. Auch die Motivation die er ausstrahlt ist ansteckend. Bitte weiter so!',
          rating: 5
        },
        {
          name: 'Jo-Ann B.',
          role: 'Product & UX Designer',
          text: 'Great experience with PSTax. Pascal is knowledgeable, professional, and incredibly kind. As an expat in Vienna, I appreciated his clear advice and support throughout the process. Highly recommended!',
          rating: 5
        },
        {
          name: 'Rory O.',
          role: 'Musician and Produzent',
          text: 'Moving to Austria was a big change, but Pascal’s expert support with Austrian taxes, social security, and my UK rental income made the transition smooth. PSTax’s professionalism and international tax expertise make them a highly recommended partner for anyone in a similar situation.',
          rating: 5,
          image: '/rory.webp'
        },
        {
          name: 'Fabio M.',
          role: 'Researcher',
          text: 'What I like is that the service is reliable and fast, with no hassles from my side. I sit back and relax.',
          rating: 5
        }
      ]
    },
    // Google reviews (home page): original wording, shortened only with "…". Source: our Google Business Profile.
    googleReviews: {
      rating: "5,0 auf Google",
      disclaimer: "Ausgewählte Bewertungen aus unserem Google-Unternehmensprofil.",
      showAll: "Alle Bewertungen anzeigen",
      starsLabel: "5 von 5 Sternen",
      carouselLabel: "Bewertungen auf Google",
      items: [
        { name: "Kerstin Spitzhofer", text: "Eine wirklich tolle Beratung und Betreuung! Pascal und sein Team sind unglaublich freundlich, hilfsbereit und nehmen sich wirklich Zeit für alle Fragen. Gerade beim Start in die Selbstständigkeit wurde ich bestens beraten und unterstützt. … absolute Weiterempfehlung!" },
        { name: "Tamara Premrov", text: "Pascal und sein Team sind kompetent, sympathisch und zuverlässig. … Pascal legt Wert darauf, einen umfassend zu informieren, sodass man seine eigene Steuererklärung auch wirklich versteht. … Toller Steuerberater, den ich gerne weiterempfehle!" },
        { name: "Michael Goldrei", text: "I used PSTax for the first time this year and they did an excellent job. Very professional, quick to respond … my previous tax advisor dropped out of doing my tax return two days before the deadline … PSTax immediately got on the case and did it in time!" },
        { name: "Agnes Schattner", text: "Es gibt Menschen, bei denen man einfach merkt, dass ihr Beruf eigentlich ihre Berufung ist – und genau das trifft auf Julia und Pascal zu. ❤️ … Eine absolute Herzensempfehlung! 🌟" },
        { name: "Rainer Spechtl", text: "… Ich war von Beginn an von seiner Kompetenz und seiner Fähigkeit, mir auch komplizierte Zusammenhänge zu vermitteln, beeindruckt. Und im Lauf der Zeit ist das Vertrauen gewachsen. Ich kann seine Kanzlei nur mit großer Überzeugung empfehlen." },
        { name: "Paula M", text: "Highly recommend 💯 They're fast, English-speaking and super friendly. But my favorite part is that I don't have to stress about SVS, Finanzamt because their team handles everything for me." },
        { name: "Nadya Kunynets", text: "Ich bin mit der Arbeit von Pascal und seinem Team sehr zufrieden! Besonders schätze ich die klare Kommunikation, die Professionalität und die unglaublich schnellen Rückmeldungen. … Für mich ist der Service jeden Cent wert. Klare Empfehlung!" },
        { name: "Pia Hettich", text: "Beste Steuerberater*innen in Wien für Selbständige. Ich bin rundum zufrieden, dankbar und happy, und kann sie nur wärmstens weiterempfehlen." },
        { name: "Marta S", text: "Pascal is one of the most knowledgeable tax advisors I know when it comes to self-employment in Austria. 👌 He is also extremely responsive, reliable and easy to communicate with. His English is excellent. …" },
        { name: "Nina K", text: "Sehr verlässliches, schnelles und freundliches Team! Man kann alles digital erledigen und auf Nachfragen wird alles verständlich erklärt, falls einem selbst das nötige Fachwissen fehlt. … Vielen Dank für die tolle Arbeit!" },
      ]
    },
    industries: {
      title: 'Für wen wir arbeiten',
      sub: 'Wir spezialisieren uns auf Dienstleister und die Kreativbranche in ganz Österreich.',
      items: [
        'IT-Freelancer', 'Software-Entwickler', 'Tech Consultants', 'Marketing-Berater',
        'Designer', 'Branding-Experten', 'Unternehmensberater', 'Coaches & Trainer',
        'Psychotherapeuten', 'Physiotherapeuten', 'Gesundheitsberufe', 'Rechtsanwälte',
        'Content Creator', 'Video- & Medienproduzenten', 'Texter & Übersetzer', 'Andere Personen-Dienstleister'
      ]
    },
    knowledgeHub: {
      tag: 'Häufige Fragen',
      title: 'Zusammenarbeit mit PSTax & Steuern in Österreich',
      sub: 'Wie wir zusammenarbeiten und einfache Erklärungen zu den häufigsten Fragen für Selbstständige.',
      footerTitle: 'Noch Fragen offen?',
      footerSub: 'Schau ob wir zusammenpassen und wir klären alles im Erstgespräch.',
      footerCta: 'Fit Check starten',
      categories: [
        'Zusammenarbeit',
        'Registrierung',
        'Einkommensteuer',
        'Sozialversicherung',
        'Umsatzsteuer',
        'Buchhaltung'
      ],
      faqs: [
        {
          category: 'Zusammenarbeit',
          question: 'Welche Schritte gibt es in der Zusammenarbeit mit PSTax?',
          answer: '1. <a href="#fit-check">Fit Check</a>: Nutze unser Online-Tool, um sicherzustellen, dass wir gut zusammenpassen.\n2. Kostenloses Erstgespräch: Wir treffen uns online, besprechen deine Situation und klären erste Fragen.\n3. Angebot: Wir schicken dir eine Zusammenfassung und unser Angebot per E-Mail.\n4. Digitales Onboarding: Ein nahtloser, 100% digitaler Prozess für dein Setup.\n5. Laufende Betreuung: Wir kümmern uns um Fristen, Einreichungen und Optimierungen.'
        },
        {
          category: 'Zusammenarbeit',
          question: 'Wie funktioniert die Kooperation während des Jahres?',
          answer: 'Wir arbeiten komplett remote und kommunizieren hauptsächlich per E-Mail. Basis-Support per E-Mail ist in allen Paketen enthalten. Dokumente tauschen wir per E-Mail, Google Drive oder über unser Klient:innenportal aus. Wir treffen uns nicht persönlich und tauschen keine physischen Ordner oder Papiere aus.'
        },
        {
          category: 'Zusammenarbeit',
          question: 'Was ist in den Jahrespaketen enthalten?',
          answer: 'Unsere Pakete decken alles ab, was du als Selbstständige:r im regulären Jahr brauchst: jährliche Steuererklärungen, Korrespondenz mit dem Finanzamt, Zahlungserinnerungen, erstmalige Finanzamtsmeldungen (inkl. UID-Nummer), proaktive Steuer- & SVS-Optimierung und laufender E-Mail-Support.'
        },
        {
          category: 'Zusammenarbeit',
          question: 'Was bedeutet "Basis-Support per E-Mail"?',
          answer: 'Wir sehen das als "Fair Use" Policy. Wir ermutigen unsere Klient:innen, sich jederzeit mit Fragen bei uns zu melden! Bitte hör auf, ChatGPT für deine Steuerfragen zu nutzen, wenn du mit uns arbeitest. Solange du uns nicht wöchtentlich eine A4-Seite voller Fragen schickst, verrechnen wir nichts extra. Falls eine Anfrage aufwendige Recherche erfordert, besprechen wir das immer vorher mit dir.'
        },
        {
          category: 'Zusammenarbeit',
          question: 'Was ist nicht in den Paketen enthalten?',
          answer: 'Bestimmte komplexe Themen sind ausgenommen: ausländische Kapitalerträge, Immobilienvermietung und offizielle Bestätigungen für Dritte (wie Vermieter oder die MA35). Zusätzliche Gespräche sind ebenfalls nicht inkludiert. Wir legen Wert auf Transparenz: Jede Leistung außerhalb des Pakets wird vorab angeboten und von dir freigegeben.'
        },
        {
          category: 'Zusammenarbeit',
          question: 'Wie hoch ist das Honorar für Leistungen außerhalb der Pakete?',
          answer: 'Für Leistungen, die nicht durch unsere Fixpakete abgedeckt sind, beträgt unser aktueller Stundensatz 270 € brutto (225 netto).'
        },
        {
          category: 'Zusammenarbeit',
          question: 'Bietet ihr auch Buchhaltung an?',
          answer: 'Unser Fokus liegt auf der jährlichen Einreichung, Optimierung und Beratung. Das Abtippen von Belegen und übertragen in Tabellen gehört nicht zu unseren Kernstärken, daher bieten wir keine klassische Buchhaltung an. Wir stellen Vorlagen bereit, mit denen du deine Einnahmen und Ausgaben selbst erfassen kannst, die wir dann optimieren. Dieser Prozess wird bald mit unserem neuen Portal inklusive automatischer Rechnungsprüfung noch einfacher.'
        },
        {
          category: 'Zusammenarbeit',
          question: 'Kann ich während des Jahres zu PSTax wechseln?',
          answer: 'Ja, ein Wechsel ist jederzeit möglich. Wir können von deiner bisherigen Beratung übernehmen oder dir helfen, alles ab Beginn des aktuellen Steuerjahres aufzuarbeiten.'
        },
        {
          category: 'Zusammenarbeit',
          question: 'Kann ich jedes beliebige Paket wählen?',
          answer: 'Die Pakete sind auf deine jeweilige Situation zugeschnitten: Wenn du unter der SVS-Grenze verdienst, bist du im Starter-Paket. Wenn du in der SVS bist, aber keine Vorsteuer abziehst, ist das Standard-Paket richtig für dich. Und wenn du in der SVS bist und Vorsteuer geltend machst, gilt das Pro-Paket. Eine freie Wahl ist daher so nicht vorgesehen.'
        },
        {
          category: 'Registrierung',
          question: 'Wie melde ich mich selbstständig?',
          answer: 'Die Anmeldung in Österreich erfolgt generell in drei Schritten:\n1. Gewerbeanmeldung (falls für deinen Beruf erforderlich)\n2. Anmeldung bei der SVS (Sozialversicherung der Selbstständigen)\n3. Anmeldung beim Finanzamt – in allen PSTax-Paketen enthalten! Wir begleiten dich bei jedem Schritt.'
        },
        {
          category: 'Registrierung',
          question: 'Benötige ich einen Gewerbeschein?',
          answer: 'Österreich ist bei der Selbstständigkeit etwas altmodisch. Die meisten Tätigkeiten erfordern einen Gewerbeschein. Das gilt besonders für IT, Marketing, Social Media, Beratung, Videoproduktion oder Content Creation. Bestimmte Berufe sind ausgenommen, wie Physiotherapeut:innen, Psychotherapeut:innen, andere Gesundheitsberufe, Lehrer:innen und Künstler:innen. Diese Gruppe nennt sich "Neue Selbstständige".'
        },
        {
          category: 'Registrierung',
          question: 'Wie melde ich ein Gewerbe an?',
          answer: 'Wenn dein Beruf unter ein freies Gewerbe fällt, kannst du es einfach online über das <a href="https://www.gisa.gv.at/at.gv.wien.fshost-gisa-at/user/formular.aspx?pid=f3cbbd2e05c54d8d889b1bddcb648fa2&pn=Bebb476a7b4514f0b90abff604e4e5fda" target="_blank" rel="noopener noreferrer">GISA Portal</a> anmelden.\n\nBitte beachte, dass du für diesen Prozess höchstwahrscheinlich eine ID Austria benötigst.\n\nFalls du ein reglementiertes Gewerbe anmelden möchtest, empfehlen wir den Kontakt zu <a href="https://miglenahofer.com/" target="_blank" rel="noopener noreferrer">Miglena Hofer</a>, einer Unternehmensberaterin mit großer Erfahrung bei Gewerbeanmeldungen in Österreich.'
        },
        {
          category: 'Registrierung',
          question: 'Wie melde ich mich bei der SVS an?',
          answer: 'Dieser Prozess hängt davon ab, ob du einen Gewerbeschein hast.\n\nMit Gewerbeschein ist die Anmeldung bei der SVS recht einfach. PSTax übernimmt das für dich, es ist in allen unseren Paketen ohne Aufpreis enthalten.\n\nAls "Neue:r Selbstständige:r" ist der Prozess komplexer. Österreich ist streng bei Scheinselbstständigkeit und prüft genau, ob du wirklich selbstständig bist. Daher muss meist ein 27-seitiger Fragebogen ausgefüllt werden.\n\nDa dies deutlich mehr Aufwand bedeutet, verrechnen wir generell eine zusätzliche Stunde, wenn du unsere Unterstützung bei diesem Prozess wünschst.\n\nIn beiden Fällen musst du entscheiden, ob du zusätzliche freiwillige Versicherungsoptionen der SVS möchtest. 99% der Selbstständigen wählen diese nicht, da die SVS ohnehin teuer genug ist – du kannst sie hier prüfen: <a href="https://www.svs.at/cdscontent/?contentid=10007.816686&portal=svsportal" target="_blank" rel="noopener noreferrer">Freiwillige SVS-Optionen</a>.'
        },
        {
          category: 'Registrierung',
          question: 'Wie melde ich mich beim Finanzamt an?',
          answer: 'Die Anmeldung beim Finanzamt muss generell innerhalb von 30 Tagen nach Aufnahme der Tätigkeit erfolgen. Das ist gesetzlich vorgeschrieben.\n\nPSTax übernimmt diesen Prozess in allen Paketen. Die Anmeldung beinhaltet meist eine grobe Schätzung von Umsatz und Gewinn für das aktuelle und das folgende Jahr.\n\nIn den meisten Fällen empfehlen wir, am Anfang eher niedrige oder konservative Schätzungen anzugeben, da diese Zahlen Vorauszahlungen auslösen können. Es ist meist ungeschickt, Steuern auf Einkommen vorauszuzahlen, das man noch gar nicht verdient hat.'
        },
        {
          category: 'Registrierung',
          question: 'Was ist ein GISA-Eintrag?',
          answer: 'GISA ist das österreichische Gewerbeinformationssystem. Viele Tätigkeiten erfordern eine formale Gewerbeberechtigung, die im GISA eingetragen sein muss, bevor man legal tätig werden darf.'
        },
        {
          category: 'Registrierung',
          question: 'Brauche ich ein separates Geschäftskonto?',
          answer: 'Es ist für Einzelunternehmer:innen nicht gesetzlich vorgeschrieben, aber wir empfehlen es durchaus. Es trennt geschäftliche und private Finanzen, was die digitale Buchhaltung deutlich schneller und sauberer macht.'
        },
        {
          category: 'Registrierung',
          question: 'Brauche ich einen Steuerberater?',
          answer: 'Es gibt in Österreich keine gesetzliche Pflicht für einen Steuerberater – du kannst alles selbst erledigen. Wenn du jedoch sichergehen willst, dass dein Business von Anfang an korrekt und steuerlich optimiert aufgesetzt ist, ist ein Gespräch mit einem Steuerberater sehr empfehlenswert.\n\nEs hilft sicherzustellen, dass keine Fristen verpasst werden und das Setup vom ersten Tag an passt. Zudem spart es dir Zeit und den Stress mit der österreichischen Bürokratie.'
        },
        {
          category: 'Einkommensteuer',
          question: 'Wo liegt die Steuerfreigrenze in Österreich?',
          answer: 'Für Selbstständige sind grob die ersten 13.000 € – 14.000 € Gewinn pro Jahr steuerfrei. Darüber fallen progressive Steuersätze ab 20% an.\n\nDiese Grenze gilt für alle Einkünfte zusammen. Wenn du z.B. Einkünfte aus Gewerbe, Angestelltentätigkeit und Vermietung hast, werden alle addiert.'
        },
        {
          category: 'Einkommensteuer',
          question: 'Wie werden Betriebsausgaben berechnet?',
          answer: 'Du kannst entweder deine tatsächlichen geschäftlichen Ausgaben abziehen (basierend auf Belegen) oder eine Pauschale nutzen.\n\nDie Basispauschale beträgt generell 20% des Umsatzes bis 55.000 € pro Jahr (darüber 15%). In manchen Branchen gelten andere Prozentsätze.'
        },
        {
          category: 'Einkommensteuer',
          question: 'Wann macht die Pauschalierung Sinn?',
          answer: 'Die Pauschalierung lohnt sich meistens dann, wenn du relativ geringe tatsächliche Betriebsausgaben hast.\n\nDu kannst einen fixen Prozentsatz deines Umsatzes abziehen, ohne jeden Beleg sammeln zu müssen. Zusätzlich zur Pauschale kannst du SVS-Beiträge, das Arbeitsplatzpauschale (Home Office) und 50% deines Klimatickets absetzen.\n\nIn unserer Erfahrung funktioniert das besonders gut für Berufe, die hauptsächlich mit dem Laptop von zu Hause arbeiten (IT, Marketing, Beratung, Content Creation).'
        },
        {
          category: 'Einkommensteuer',
          question: 'Was gilt als Betriebsausgabe?',
          answer: 'Generell alles, was du für dein Business kaufst. Das Finanzamt achtet jedoch darauf, dass Privat- und Geschäftsausgaben nicht vermischt werden.\n\nTypische Ausgaben sind Internet, Laptop und Equipment, Büromiete, Software-Abos, Werbung, Reisekosten (sofern rein geschäftlich), Material, Honorare für Freelancer und größere Anschaffungen wie Kameras.'
        },
        {
          category: 'Einkommensteuer',
          question: 'Muss ich Rechnungen und Belege aufbewahren?',
          answer: 'Wenn du tatsächliche Ausgaben absetzt, solltest du alle Belege griffbereit haben, falls das Finanzamt sie anfordert.\n\nBei PSTax musst du uns nicht jeden Beleg proaktiv schicken. Wir fordern Dokumente an, wenn etwas unklar ist, und erwarten, dass du sie für Prüfungen bereit hältst – was in der Praxis bei Einzelunternehmen nicht allzu oft vorkommt.'
        },
        {
          category: 'Einkommensteuer',
          question: 'Wann müssen Steuern gezahlt werden?',
          answer: 'Nach Jahresende werden deine Steuern basierend auf dem tatsächlichen Gewinn neu berechnet. Sobald der Steuerbescheid kommt, ist die Zahlung meist innerhalb eines Monats fällig.\n\nDas Ergebnis aktualisiert meist auch deine Vorauszahlungen für das Folgejahr. Diese sind quartalsweise am 15. Februar, 15. Mai, 15. August und 15. November fällig.'
        },
        {
          category: 'Einkommensteuer',
          question: 'Was ist die Frist für die Steuererklärung?',
          answer: 'Wenn du deine Steuern selbst einreichst, hast du meist bis zum 30. Juni des Folgejahres Zeit (via FinanzOnline).\n\nWenn du mit einem Steuerberater arbeitest, erhältst du meist eine automatische Verlängerung und hast effektiv bis zu ein volles Jahr länger Zeit.'
        },
        {
          category: 'Sozialversicherung',
          question: 'Was ist die SVS?',
          answer: 'Die SVS ist die österreichische Sozialversicherungsanstalt der Selbstständigen.\n\nWenn du voll selbstständig bist, bist du über die SVS kranken-, pensions- und unfallversichert.\n\nWenn du nur nebenberuflich selbstständig bist und hauptberuflich angestellt bleibst, musst du – je nach Gewerbestatus – eventuell nur die Unfallversicherung (ca. 13 € pro Monat) statt der vollen Beiträge zahlen.'
        },
        {
          category: 'Sozialversicherung',
          question: 'Wie hoch sind die SVS-Beiträge?',
          answer: 'SVS-Beiträge liegen bei ca. 27% deines Gewinns und beinhalten Kranken-, Pensions- und Unfallversicherung.\n\nAls Faustregel für Dienstleister ergibt das oft ca. 20% vom Umsatz, wenn man die Pauschalierung nutzt.\n\nDu kannst unseren <a href="#calculator">SVS- und Steuerrechner</a> nutzen, um deine Beiträge zu schätzen.'
        },
        {
          category: 'Sozialversicherung',
          question: 'Wie hoch sind die Beiträge in den ersten drei Jahren?',
          answer: 'Das SVS-System funktioniert anders, als viele erwarten.\n\nIn den ersten drei Jahren zahlst du meist reduzierte vorläufige Beiträge von etwas unter 500 € pro Quartal für die volle Versicherung.\n\nDas sind jedoch nur Anzahlungen. Später berechnet die SVS die tatsächlichen Beiträge basierend auf deinem realen Gewinn dieses Jahres.\n\nSVS funktioniert also wie ein Vorauszahlungssystem. Wenn dein Gewinn ca. 6.600 € übersteigt, kommt es später meist zu Nachzahlungen.'
        },
        {
          category: 'Sozialversicherung',
          question: 'Gibt es einen reduzierten SVS-Satz am Anfang?',
          answer: 'Es gibt einen reduzierten Krankenversicherungssatz für Gewerbetreibende in den ersten zwei Jahren.\n\nDas ist jedoch nicht dasselbe wie die reduzierte Beitragsgrundlage der ersten drei Jahre. Zudem gilt der reduzierte KV-Satz nicht für "Neue Selbstständige".'
        },
        {
          category: 'Sozialversicherung',
          question: 'Was ist der SVS-Mindestbeitrag?',
          answer: 'Selbst mit wenig oder gar keinem Gewinn gibt es meist einen Mindestbeitrag für Kranken- und Pensionsversicherung, sofern keine Ausnahme vorliegt.\n\nEine Ausnahme ist meist nur möglich, wenn dein Gewinn unter ca. 6.600 € liegt und du bereits anderweitig versichert bist (z.B. durch Anstellung), also keine volle SVS-Versicherung benötigst.'
        },
        {
          category: 'Sozialversicherung',
          question: 'Wie vermeide ich SVS-Nachzahlungen?',
          answer: 'Bei PSTax achten wir stark auf die SVS: Wir beobachten deinen Umsatz und deine Ausgaben, um deine SVS-Last zu prognostizieren.\n\nAm Jahresende kontaktieren wir unsere Klient:innen, um auf die SVS-Last hinzuweisen. Oft macht eine freiwillige Vorauszahlung Sinn.\n\nDas verhindert böse Überraschungen Jahre später und dient als wichtige Steuerabsetzung. Wenn du z.B. 5.000 € extra an die SVS vorauszahlst, sparst du eventuell 2.000 € an Steuern für das Folgejahr (je nach Einkommen).'
        },
        {
          category: 'Sozialversicherung',
          question: 'Kann ich aus der SVS austreten?',
          answer: 'Wenn Umsatz und Gewinn unter bestimmten Grenzen liegen, kannst du eine Ausnahme von der Kranken- und Pensionsversicherung beantragen und bleibst nur unfallversichert – es müssen jedoch auch andere Kriterien erfüllt sein!'
        },
        {
          category: 'Umsatzsteuer',
          question: 'Was ist die Kleinunternehmergrenze?',
          answer: 'Wenn dein jährlicher Umsatz unter 55.000 € liegt, giltst du als Kleinunternehmen. Du musst keine Umsatzsteuer verrechnen, darfst aber auch keine Vorsteuer von deinen Einkäufen abziehen. Die 55.000 € Grenze gilt für den österreichischen Umsatz. Internationaler B2B-Umsatz zählt nicht zu dieser Grenze!'
        },
        {
          category: 'Umsatzsteuer',
          question: 'Brauche ich eine UID-Nummer?',
          answer: 'Es gibt generell drei Situationen, in denen du eine UID-Nummer in Österreich brauchst:\n\n1. Dein Umsatz in Österreich übersteigt 55.000 € pro Jahr.\n\n2. Du machst Geschäfte mit anderen EU-Unternehmen. Hier brauchst du eine UID und musst ZM-Meldungen abgeben.\n\n3. Du optierst freiwillig in die USt, um Vorsteuer abzuziehen.'
        },
        {
          category: 'Umsatzsteuer',
          question: 'Wie bekomme ich eine UID-Nummer?',
          answer: 'Du beantragst sie beim Finanzamt. Sie ist essenziell für B2B-Kunden in der EU. PSTax übernimmt das für dich in allen Paketen kostenlos!'
        },
        {
          category: 'Umsatzsteuer',
          question: 'Was ist der Unterschied zwischen UID-Nummer haben und "USt-pflichtig" sein?',
          answer: 'Eine UID-Nummer zu haben bedeutet nicht automatisch, dass du voll USt-pflichtig bist. Wenn dein Umsatz unter 55.000 € liegt, bist du immer noch Kleinunternehmen und nicht zum Vorsteuerabzug berechtigt, außer du optierst zur Regelbesteuerung.'
        },
        {
          category: 'Umsatzsteuer',
          question: 'Option zur Regelbesteuerung',
          answer: 'Eine UID-Nummer zu haben bedeutet nicht automatisch, dass du voll im Umsatzsteuersystem bist.\n\nWenn dein Umsatz in Österreich unter 55.000 € liegt, bist du Kleinunternehmen und nicht vorsteuerabzugsberechtigt. Um Vorsteuer vom Finanzamt zurückzuerhalten, musst du freiwillig zur Regelbesteuerung optieren.\n\nDas kann Sinn machen, wenn du hauptsächlich B2B-Kunden hast und viele Ausgaben mit Vorsteuer planst.\n\nWir empfehlen das aber erst ab ca. 500 € Vorsteuer pro Jahr, da es Mehraufwand bedeutet: Quartalsmeldungen, quartalsweise Buchhaltungsmeldungen, USt-Jahreserklärung. Für viele lohnt sich das nicht.\n\nZudem bindet dich die Option generell für fünf Jahre – auch wenn du das Businessmodell änderst.'
        },
        {
          category: 'Umsatzsteuer',
          question: 'Was ist Reverse Charge?',
          answer: 'Reverse Charge bedeutet, dass der B2B-Kunde im EU-Ausland die Steuer in seinem Land abführt. Das vereinfacht Rechnungen für Selbstständige. Du musst den Hinweis "Übergang der Steuerschuldnerschaft (Reverse Charge)" auf die Rechnung schreiben.'
        },
        {
          category: 'Umsatzsteuer',
          question: 'Wann müssen USt-Voranmeldungen abgegeben werden?',
          answer: 'Je nach Vorjahresumsatz werden UVAs entweder quartalsweise (Standard) oder monatlich (ab 100.000 € Umsatz) via FinanzOnline abgegeben.'
        },
        {
          category: 'Umsatzsteuer',
          question: 'Ist die USt-Voranmeldung in den PSTax-Paketen enthalten?',
          answer: 'Wir inkludieren UVAs generell nicht in den Standardpaketen, da viele Klient:innen sie selbst machen.\n\nDaher bieten wir sie als Zusatzleistung für 50 € pro Quartal an.'
        },
        {
          category: 'Buchhaltung',
          question: 'Was muss eine gültige Rechnung enthalten?',
          answer: 'Deine Daten, Kundendaten, eine eindeutige Rechnungsnummer (Tipp: Jahr inkludieren, z.B. "2026-001"), Datum, Leistungsbeschreibung, Nettobetrag, Steuersatz/-betrag und deine UID (falls vorhanden).'
        },
        {
          category: 'Buchhaltung',
          question: 'Wie lange muss ich Belege aufbewahren?',
          answer: 'In Österreich musst du alle geschäftlichen Unterlagen, Belege und Rechnungen mindestens 7 Jahre lang aufbewahren. Digitale Aufbewahrung ist legal und empfehlenswert.'
        },
        {
          category: 'Buchhaltung',
          question: 'Brauche ich spezielle Software?',
          answer: 'Nein. Cloud-Tools können für manche hilfreich sein, aber wir bieten Excel/Google Sheets Vorlagen an, die speziell für Solo-Dienstleister:innen in Österreich entwickelt wurden. Du fügst einfach eine Zeile pro Einnahme/Ausgabe hinzu und wählst eine Kategorie. Bei Fragen markierst du uns in einem Kommentar.'
        },
        {
          category: 'Buchhaltung',
          question: 'Sind digitale Belege erlaubt?',
          answer: 'Ja! Das Finanzamt akzeptiert digitale Versionen, solange sie authentisch, unverändert und lesbar über den 7-jährigen Zeitraum aufbewahrt werden.'
        }
      ]
    }
  }
};

// Keyed by pathname (both DE and EN keys point at the same pair) so the
// language switcher (Navbar.tsx) and GuideLanguageSwitch.tsx can look up
// "where does this exact page live in the other language" from
// `location.pathname` alone. Every entry uses the site's DE/EN URL scheme:
// German under /de/, English under /en/, both with a trailing slash (see
// main-site/src/contexts/LanguageContext.tsx and App.tsx's route table).
export const LANGUAGE_ROUTE_PAIRS: Record<string, { EN: string; DE: string }> = {
  '/de/': { EN: '/en/', DE: '/de/' },
  '/en/': { EN: '/en/', DE: '/de/' },
  '/de/guides/': { EN: '/en/guides/', DE: '/de/guides/' },
  '/en/guides/': { EN: '/en/guides/', DE: '/de/guides/' },
  '/de/guides/svs-sozialversicherung/kurz/': { EN: '/en/guides/svs-social-insurance/short/', DE: '/de/guides/svs-sozialversicherung/kurz/' },
  '/en/guides/svs-social-insurance/short/': { EN: '/en/guides/svs-social-insurance/short/', DE: '/de/guides/svs-sozialversicherung/kurz/' },
  '/de/guides/umsatzsteuer-grundlagen/kurz/': { EN: '/en/guides/vat-basics-austria/short/', DE: '/de/guides/umsatzsteuer-grundlagen/kurz/' },
  '/en/guides/vat-basics-austria/short/': { EN: '/en/guides/vat-basics-austria/short/', DE: '/de/guides/umsatzsteuer-grundlagen/kurz/' },
  '/de/pstax-portal-about/': { EN: '/en/pstax-portal-about/', DE: '/de/pstax-portal-about/' },
  '/en/pstax-portal-about/': { EN: '/en/pstax-portal-about/', DE: '/de/pstax-portal-about/' },
  '/de/pstax-portal-about-long/': { EN: '/en/pstax-portal-about-long/', DE: '/de/pstax-portal-about-long/' },
  '/en/pstax-portal-about-long/': { EN: '/en/pstax-portal-about-long/', DE: '/de/pstax-portal-about-long/' },
  '/de/impressum/': { EN: '/en/imprint/', DE: '/de/impressum/' },
  '/en/imprint/': { EN: '/en/imprint/', DE: '/de/impressum/' },
  '/de/datenschutz/': { EN: '/en/privacy-policy/', DE: '/de/datenschutz/' },
  '/en/privacy-policy/': { EN: '/en/privacy-policy/', DE: '/de/datenschutz/' },
  '/de/agb/': { EN: '/en/gtc/', DE: '/de/agb/' },
  '/en/gtc/': { EN: '/en/gtc/', DE: '/de/agb/' },
  '/de/guides/svs-sozialversicherung/': { EN: '/en/guides/svs-social-insurance/', DE: '/de/guides/svs-sozialversicherung/' },
  '/en/guides/svs-social-insurance/': { EN: '/en/guides/svs-social-insurance/', DE: '/de/guides/svs-sozialversicherung/' },
  '/de/guides/selbststaendig-werden/': { EN: '/en/guides/starting-out-self-employed/', DE: '/de/guides/selbststaendig-werden/' },
  '/en/guides/starting-out-self-employed/': { EN: '/en/guides/starting-out-self-employed/', DE: '/de/guides/selbststaendig-werden/' },
  '/de/guides/umsatzsteuer-grundlagen/': { EN: '/en/guides/vat-basics-austria/', DE: '/de/guides/umsatzsteuer-grundlagen/' },
  '/en/guides/vat-basics-austria/': { EN: '/en/guides/vat-basics-austria/', DE: '/de/guides/umsatzsteuer-grundlagen/' },
  '/de/guides/kleinunternehmerregelung/': { EN: '/en/guides/small-business-vat-exemption/', DE: '/de/guides/kleinunternehmerregelung/' },
  '/en/guides/small-business-vat-exemption/': { EN: '/en/guides/small-business-vat-exemption/', DE: '/de/guides/kleinunternehmerregelung/' },
};

export const INDUSTRIES = [
  'IT freelancers', 'Software developers', 'Tech consultants', 'Marketing consultants',
  'Designers', 'Advertising & branding professionals', 'Business consultants', 'Coaches & trainers',
  'Psychotherapists', 'Physiotherapists', 'Health service professionals', 'Lawyers',
  'Content creators', 'Video / media producers', 'Writers & translators', 'Other solo service businesses'
];

export const TEAM = [
  {
    name: 'Pascal Schraml',
    role: 'Managing Director & Tax Advisor',
    desc: 'Pascal leads PSTax and specializes in tax advisory for self-employed people, freelancers and digital service businesses in Austria.',
    image: '/pascal-team.webp'
  },
  {
    name: 'Julia Kauer',
    role: 'Client & Internal Operations',
    desc: 'Julia keeps client work moving smoothly, coordinates digital workflows and supports clients with tax, SVS and administrative questions.',
    image: '/julia.webp'
  },
  {
    name: 'Evelyn Pfannhauser',
    role: 'Tax Advisor Candidate',
    desc: 'Evelyn supports clients with tax returns, VAT topics, research and communication with Austrian authorities.',
    image: '/regenerated_image_1777647524692.webp'
  },
  {
    name: 'Lisa Thaller',
    role: 'Tax Advisor Candidate',
    desc: 'Lisa supports all ongoing tax matters and the preparation of tax returns — carefully, reliably and with attention to detail.',
    image: '/lisa.jpg'
  },
  {
    name: 'Thomas Wöpperer',
    role: 'Technology, Security & Automation',
    desc: 'Thomas manages security, internal systems and development to keep our digital workflows reliable, efficient and secure.',
    image: '/thomas.jpg'
  }
];

export const REVIEWS: Review[] = [
  {
    name: 'Ana T.',
    role: 'Dancer',
    text: 'Excellent! Fast replies, very helpful, simple and clear instructions, advices and clarifications for any question you might have. The team works fast, gives security and feels like being taken care of in the world of papers. :)  Totally Recommended!',
    rating: 5
  },
  {
    name: 'Amaia S.',
    role: 'Leadership Coach & Trainer',
    text: 'I could not recommend this tax advisor more. Not only is Pascal great at what he does, but he gives you such a sense of support, care and professionality that you feel reassured - which is so helpful when you are an entrepreneur! The human aspect he brings to the table makes the whole difference.',
    rating: 5
  },
  {
    name: 'Hannes H.',
    role: 'Pflegegewerbe',
    text: 'Ich würde PSTax jedem weiterempfehlen. Pascal und sein Team sind immer für einen da, wenn man Fragen hat. Es ist immer wieder erstaunlich wie schnell die Antworten zu gestellten Fragen kommen. Auch die Motivation die er ausstrahlt ist ansteckend. Bitte weiter so!',
    rating: 5
  },
  {
    name: 'Jo-Ann B.',
    role: 'Product & UX Designer',
    text: 'Great experience with PSTax. Pascal is knowledgeable, professional, and incredibly kind. As an expat in Vienna, I appreciated his clear advice and support throughout the process. Highly recommended!',
    rating: 5
  },
  {
    name: 'Rory O.',
    role: 'Musician and producer',
    text: 'Moving to Austria was a big change, but Pascal’s expert support with Austrian taxes, social security, and my UK rental income made the transition smooth. PSTax’s professionalism and international tax expertise make them a highly recommended partner for anyone in a similar situation.',
    rating: 5,
    image: '/rory.webp'
  },
  {
    name: 'Fabio M.',
    role: 'Researcher',
    text: 'What I like is that the service is reliable and fast, with no hassles from my side. I sit back and relax.',
    rating: 5
  }
];

export const FAQS: FAQItem[] = [
  // Working with PSTax
  {
    category: 'Working with PSTax',
    question: 'What are the steps to work with PSTax?',
    answer: '1. <a href="#fit-check">Fit Check</a>: Use our online tool to ensure we are the right match for your needs.\n2. Free Consultation: We meet online, discuss your situation and answer first questions on the call. \n3. We send you a summary of our call and our offer via email. \n4. Digital Onboarding: A seamless, 100% digital process to get you set up.\n5. We start working together with year-round support: We manage your deadlines, filings, and optimizations.'
  },
  {
    category: 'Working with PSTax',
    question: 'How does the cooperation during the year work?',
    answer: 'We operate fully remote and primarily communicate via email. Basic email support is included in all our packages. We exchange documents through via E-Mail, Google Drive or our client portal. We do not meet in person or exchange physical folders/papers.'
  },
  {
    category: 'Working with PSTax',
    question: 'What is included in the annual packages?',
    answer: 'Our packages are designed to cover everything you need as self-employed person during a regular year: annual tax filings, tax office correspondence, payment reminders, initial tax office registrations (indluing the VAT ID request), proactive tax & SVS optimization, and ongoing email support.'
  },
  {
    category: 'Working with PSTax',
    question: 'What does "basic email support" mean?',
    answer: 'We view this as a "fair use" policy. We encourage all our clients to reach out with your questions anytime! Please stop using ChatGPT to answer your tax question, if you work with us! As long as you aren’t sending us an A4 page of questions every week, we won’t charge extra. If a request requires significant research, we will always discuss this with you beforehand.'
  },
  {
    category: 'Working with PSTax',
    question: 'What is not included in the packages?',
    answer: 'Certain complex topics are excluded: foreign capital investments, rental properties, and official written confirmations for third parties (like landlords or MA35). Additional calls are also not included in our packages. We value transparency: any out-of-scope work is quoted and approved by you before we start.'
  },
  {
    category: 'Working with PSTax',
    question: 'What is the fee for out-of-scope services?',
    answer: 'For services not covered by our fixed packages, our current hourly rate is € 270 gross (€ 225 net).'
  },
  {
    category: 'Working with PSTax',
    question: 'Do you offer bookkeeping services?',
    answer: 'We focus on annual filing, optimization and support. Data-entry into spreadsheets is therefore not our main strength and we do not offer bookkeeping as such. We provide templates for you to track your own income and expenses, which we then optimize. This process will soon be even smoother with our new client portal featuring automatic invoice recognition.'
  },
  {
    category: 'Working with PSTax',
    question: 'Can I switch to PSTax during the year?',
    answer: 'Yes, switching is possible at any time. We can take over from your previous advisor or help you catch up on everything from the start of the current tax year.'
  },
  {
    category: 'Working with PSTax',
    question: 'Can I choose any package?',
    answer: 'The packages are designed to match your current situation: if you earn below the SVS threshold, then you are in the Starter Package. If you are in SVS, but not claiming input VAT, the Standard Package is the one for you. And if you are in SVS and also claiming input VAT, the Pro Package applies. Therefore choosing a package is as such not intended. '
  },

  // Getting Started
  {
    category: 'Registering in Austria',
    question: 'How do I register as self-employed?',
    answer: 'Being self-employed in Austria generally requires three steps:\n1. Registering a business license (if your profession requires one)\n2. Registration with the SVS (social security institution for the self-employed)\n3. Registration with the tax office (Finanzamt) — included in all PSTax packages! We guide you through each step.'
  },
  {
    category: 'Registering in Austria',
    question: 'Do I need a business license?',
    answer: 'Austria is a bit old-fashioned when it comes to self-employment. Most people will need a business license. This is especially true if you work in IT, marketing, social media, consulting, video production, or content creation. Certain professions are exempt, such as physiotherapists, psychotherapists, other health professionals, teachers, and artists. This group is called Newly Self Employed or "Neue Selbständige".'
  },
  {
    category: 'Registering in Austria',
    question: 'How do I register a business license?',
    answer: 'If your profession falls under a free business license, you can simply register it via the <a href="https://www.gisa.gv.at/at.gv.wien.fshost-gisa-at/user/formular.aspx?pid=f3cbbd2e05c54d8d889b1bddcb648fa2&pn=Bebb476a7b4514f0b90abff604e4e5fda" target="_blank" rel="noopener noreferrer">GISA Portal</a>.\n\nPlease note that you will most likely need an ID Austria for this process.\n\nIf you need a regulated business license, I highly recommend reaching out to <a href="https://miglenahofer.com/" target="_blank" rel="noopener noreferrer">Miglena Hofer</a>, a business consultant with extensive experience helping people get registered and set up in Austria.'
  },
  {
  category: 'Registering in Austria',
  question: 'How do I register at the SVS?',
  answer: 'This process depends on your status as a business license holder.\n\nIf you have a business license, the registration with the SVS is fairly simple. PSTax takes care of this for you and it is included in all PSTax packages at no extra charge.\n\nIf you are registered as a “Neuer Selbstständiger”, the registration process is more complex. Austria is very strict when it comes to bogus self-employment and wants to ensure that you are genuinely self-employed and should not actually qualify as an employee. Therefore, you usually need to complete a 27-page questionnaire.\n\nSince this requires significantly more work, we generally charge one additional hour if you would like our support with this process.\n\nIn both cases, you will also need to decide whether you want additional voluntary insurance options from the SVS. Generally speaking, 99% of self-employed people do not choose these options because the SVS is already expensive enough as it is — but you can check them out here: <a href="https://www.svs.at/cdscontent/?contentid=10007.816686&portal=svsportal" target="_blank" rel="noopener noreferrer">SVS Voluntary Insurance Options</a>.'
  },
  {
    category: 'Registering in Austria',
    question: 'How do I register with the tax office?',
    answer: 'The tax office registration should generally be completed within 30 days of starting your business activities. Austrian law requires all self-employed business activities to be registered with the tax office within this timeframe.\n\nPSTax takes care of this process in all of our packages. The registration usually involves providing the tax office with a rough estimate of your expected revenue and profit for the current and following year.\n\nIn most cases, we recommend using lower or very conservative estimates in the beginning, as these numbers may trigger income tax prepayments. In our experience, it is quite unfortunate to make tax prepayments on income that you have not actually earned yet.'
  },
  {
    category: 'Registering in Austria',
    question: 'What is a GISA entry?',
    answer: 'GISA is the Austrian Business License Information System. Many activities in Austria require a formal trade license which is registered in GISA before you can legally operate.'
  },
  {
    category: 'Registering in Austria',
    question: 'Do I need a separate business bank account?',
    answer: 'While not strictly mandatory for sole traders, we usually recommended it. It keeps your business and personal finances separate, making digital bookkeeping much faster and cleaner.'
  },
  {
    category: 'Registering in Austria',
    question: 'Do I need a tax advisor?',
    answer: 'There is no legal requirement to have a tax advisor in Austria — you can handle everything yourself if you prefer. However, if you want to make sure your business is set up correctly and in a tax-efficient way from the beginning, it is highly recommended to speak with a tax advisor before starting your activities.\n\nThis helps ensure that you do not miss important tax or SVS registration deadlines and that your setup is optimized from day one. Of course, it also saves you the time and stress of dealing with Austrian tax bureaucracy yourself.'
  },

  // Income Tax
  {
    category: 'Income Tax',
    question: 'What is the tax-free threshold in Austria?',
    answer: 'For self-employed individuals, roughly the first € 13.000 – 14.000 of annual profit is tax-free. Above this amount, progressive income tax rates apply starting at 20%.\n\nThis tax threshold applies to all types of income combined. For example, if you have income from self-employment, employment, or rental income, all of these are added together for income tax purposes.'
  },
  {
    category: 'Income Tax',
    question: 'How are business expenses calculated?',
    answer: 'You can either deduct your actual business-related expenses (based on receipts and invoices) or use a flat-rate deduction (“Pauschale”).\n\nThe flat-rate deduction is generally 20% of your revenue up to € 55.000 per year and 15% above this threshold. In certain rare industries, other flat-rate percentages may apply.'
  },
  {
    category: 'Income Tax',
    question: 'When does the flat-rate deduction make sense?',
    answer: 'In general, the flat-rate deduction works best if you have relatively low actual business expenses.\n\nIt allows you to deduct a fixed percentage of your revenue as a business expense without having to collect receipts for every small purchase. On top of the flat-rate deduction, you can still deduct SVS contributions, a work-from-home allowance, and 50% of your annual public transport ticket.\n\nIn our experience, this works particularly well for professions that mainly work from home with a laptop, such as IT, marketing, consulting, social media, or content creation.'
  },
  {
    category: 'Income Tax',
    question: 'What is considered a business expense?',
    answer: 'In general, anything you purchase for your business can potentially qualify as a business expense. However, the tax office wants to ensure that private and business expenses are not mixed.\n\nTypical business expenses include internet costs, laptops and equipment, office rent, software subscriptions, advertising costs, travel expenses (if clearly business-related), goods and materials, payments to freelancers, and larger assets such as cameras or other work equipment.'
  },
  {
    category: 'Income Tax',
    question: 'Do I need to keep invoices and receipts?',
    answer: 'If you decide to deduct your actual business expenses, you should keep all invoices and receipts available in case the tax office requests them.\n\nWhen working with PSTax, you generally do not need to proactively send us every receipt. We will request documents if something is unclear, and we expect you to have the receipts ready in case of a tax office review or audit — although in practice, it does not happen to often.'
  },
  {
    category: 'Income Tax',
    question: 'When are tax payments due?',
    answer: 'After the end of the year, your taxes are recalculated based on your actual annual profit. Once you receive your tax assessment (“Steuerbescheid”), the payment is generally due within one month.\n\nThe result of this assessment will also update your tax prepayments for the following year, meaning higher profits may lead to higher quarterly prepayments in the future. Prepayments are due quarterly on February 15, May 15, August 15, and November 15.'
  },
  {
    category: 'Income Tax',
    question: 'What is the deadline for filing taxes?',
    answer: 'If you file your taxes yourself, you generally have until June 30th of the following year to submit them via FinanzOnline.\n\nIf you work with a tax advisor, you usually receive an automatic extension and effectively have up to one full additional year to file your taxes.'
  },

  // Social Security
  {
    category: 'Social Security',
    question: 'What is SVS?',
    answer: 'SVS is the Austrian social security institution for self-employed individuals.\n\nIf you are fully self-employed, you will generally have health, pension, and accident insurance through the SVS.\n\nIf you are only self-employed part-time and still have a regular employment as your main job, you might — depending on your business license status — only have to pay accident insurance (roughly € 13 per month) instead of the full insurance contributions.'
  },
  {
    category: 'Social Security',
    question: 'How much are SVS contributions?',
    answer: 'SVS contributions are roughly 27% of your profit and include health, pension, and accident insurance.\n\nAs a rule of thumb for service-based industries, this usually works out to around 20% of your revenue, assuming you use the flat-rate expense deduction (“Pauschale”).\n\nYou can use our <a href="#calculator">SVS and Tax Calculator</a> to estimate your own contributions.'
  },
  {
    category: 'Social Security',
    question: 'What are the SVS rates during the first three years?',
    answer: 'It is important to understand that the SVS charging system works a little differently than most people expect.\n\nDuring the first three years of self-employment, you are generally initially charged reduced preliminary contributions of slightly below € 500 per quarter for full insurance coverage (health, pension, and accident insurance).\n\nHowever, these are only preliminary payments. Later on, the SVS recalculates your actual contributions based on the real profit you earned during that year.\n\nThis means that SVS effectively works on a prepayment system, similar to the tax office. If your actual annual profit exceeds roughly € 6.600, you will usually receive an additional top-up payment later on.'
  },
  {
    category: 'Social Security',
    question: 'Is there a reduced SVS rate in the first years?',
    answer: 'There is a reduced health insurance rate for business license holders (“Gewerbetreibende”) during the first two years of self-employment.\n\nHowever, this is not the same thing as the reduced contribution basis that applies during the first three years. Also, this reduced health insurance rate does not apply to “Neue Selbstständige”.'
  },
  {
    category: 'Social Security',
    question: 'What is the SVS minimum contribution?',
    answer: 'Even with little or no profit, there is generally a minimum contribution for health and pension insurance unless you qualify for an exemption.\n\nAn exemption is usually only possible if your annual profit is below roughly € 6.600 and you already have another form of insurance coverage available, meaning you do not actually need full SVS insurance.'
  },
  {
    category: 'Social Security',
    question: 'How to avoid SVS back payments?',
    answer: 'At PSTax we have a very strong focus on SVS: We monitor your current revenue and expenses to forecast your expected SVS liability. \n\nAt the end of the year we contact all our clients to make sure they are aware of their actual SVS liability and often it will make sense to do a voluntary prepayment to the SVS. \n\nThis helps avoid unpleasant surprises and large back-payments several years later plus it serves as a very important tax deduction. If you prepay for example an extra € 5.000 to the SVS you might be saving € 2.000 on next years tax bill (depending on you income).'
  },
  {
    category: 'Social Security',
    question: 'Can I opt out of SVS?',
    answer: 'If your revenue and profit are below certain thresholds, you can apply for an exemption from health and pension insurance and remain covered only by accident insurance - but other requirements have to be met as well!'
  },

  // VAT
  {
    category: 'VAT',
    question: 'What is the small business threshold?',
    answer: 'If your annual revenue is below € 55.000, you are considered a "Kleinunternehmer" and don\'t have to charge VAT, but you also can\'t deduct input VAT on your own purchases. The € 55.000 threshold applies to your Austrian revenue. International B2B revenue does not count against this threshold!'
  },
  {
  category: 'VAT',
  question: 'Do I need a VAT ID number (UID number)?',
  answer: 'There are generally only three situations in which you need a VAT ID number (“UID number”) in Austria:\n\n1. Your Austrian revenue exceeds € 55.000 per year, meaning you are required to charge VAT to your clients.\n\n2. You do business with other EU businesses. In this case, you usually need a VAT ID number and must report this revenue quarterly via the EU recapitulative statement (“Zusammenfassende Meldung”).\n\n3. You voluntarily opt into the VAT system because you want to benefit from input VAT deduction (“Vorsteuerabzug”).'
  },
  {
    category: 'VAT',
    question: 'How do I get a VAT ID (UID) number?',
    answer: 'You apply for a VAT Number (UID) at the tax office. It is essential if you work with B2B clients in other EU countries. PSTax takes care of this for you in all packages at no extra charge!'
  },
  {
    category: 'VAT',
    question: 'Is there a difference between having a VAT ID and being “in VAT”?',
    answer: 'Having a VAT ID does not automatically mean you are fully into VAT. If your Austrian revenue is below € 55.000 you are still considered a small entrepreneur, and are not entitled to input VAT deduction (Vorsteuerabzug). This would require you to opt into the VAT system.'
  },
  {
    category: 'VAT',
    question: 'Opting into the VAT system',
    answer: 'Having a VAT ID number does not automatically mean that you are fully part of the VAT system.\n\nIf your Austrian revenue is below € 55.000 per year, you are generally still considered a small entrepreneur (“Kleinunternehmer”) and are therefore not entitled to input VAT deduction (“Vorsteuerabzug”). To reclaim VAT from the tax office, you would need to voluntarily opt into the VAT system.\n\nOpting into VAT can make sense if you mainly work with B2B clients and have a significant amount of input VAT that you can deduct (meaning VAT that you can reclaim from the tax office).\n\nHowever, we usually do not recommend opting into VAT unless you expect at least around € 500 of deductible input VAT per year. This is because opting into VAT creates additional administrative work:\n\n- Quarterly VAT filings\n- Quarterly bookkeeping reporting\n- An annual VAT return\n- More ongoing compliance and administration\n\nFor many beginners, this additional overhead is simply not worth it.\n\nIt is also important to understand that voluntarily opting into the VAT system generally binds you for five years — even if you later change industries or business models.\n\nFor example, if you later decide to open a bakery and sell to private consumers, you would still need to charge VAT during this period.'
  },
  {
    category: 'VAT',
    question: 'What is Reverse Charge?',
    answer: 'Reverse Charge means the B2B client in another EU country pays the VAT in their own country. It simplifies cross-border billing for self-employed professionals. You need to put the note "This invoice is subject to Reverse Charge" on your invoices in that case.'
  },
  {
    category: 'VAT',
    question: 'When do I need to file VAT returns?',
    answer: 'Depending on your previous year\'s revenue, VAT returns (UVA) are filed either quarterly (standard) or monthly (if last years revenue is greater than € 55.000) via FinanzOnline.'
  },
  {
  category: 'VAT',
  question: 'Is VAT filing included in PSTax packages?',
  answer: 'We generally do not include quarterly VAT filings (“UVA” reports) in our standard packages, as many clients prefer to handle the reporting themselves.\n\nTherefore, VAT reporting is offered as a separate service for € 50 per quarterly filing.\n\nDepending on your previous year’s revenue, VAT returns are filed either quarterly (standard case) or monthly if your previous year’s revenue exceeded € 100.000.'
  },

  // Bookkeeping and invoices
  {
    category: 'Bookkeeping and invoices',
    question: 'What must a valid invoice contain?',
    answer: 'An invoice must include your details, client details, a unique invoice number (pro tip: include the year in the invoice number: e.g. "2026-001"), date, description of services, the net amount, VAT rate/amount, and your UID number (if you have one).'
  },
  {
    category: 'Bookkeeping and invoices',
    question: 'How long must I keep records?',
    answer: 'In Austria, you must keep all business-related records, receipts, and invoices for at least 7 years. Digital storage is legal and preferred.'
  },
  {
    category: 'Bookkeeping and invoices',
    question: 'Do I need special software?',
    answer: 'No. While cloud tools are helpful, we provide custom Excel/Google Sheets templates specifically designed for the needs of solo service businesses in Austria. You simply add 1 line per income/expense and choose a category. If you need help you just tag us in a comment and we get an email and can assist you.'
  },
  {
    category: 'Income Tax',
    question: 'Do I need to send invoices and receipts to PSTax?',
    answer: 'If you decide to deduct your actual business expenses, you should keep all invoices and receipts available in case the tax office requests them.\n\nWhen working with PSTax, you generally do not need to proactively send us every receipt. We will request documents if something is unclear, and we expect you to have the receipts ready in case of a tax office review or audit — although in practice, it does not happen to often.'
  },
  {
    category: 'Bookkeeping and invoices',
    question: 'Are digital receipts allowed?',
    answer: 'Yes! The tax office accepts digital versions of receipts as long as they are authentic, unchanged, and readable throughout the 7-year retention period.'
  }
];
